import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { ListingTypeOptions } from "@/utils/productTypes";
import {
  type ShopListingModel,
  sortOptions,
} from "pages/root/shop/FilterAndSortListings";

import type { ListingSortOptions } from "./FilterAndSortListings";

const DEFAULT_SEARCH_PARAMS = {
  areas: "",
  concerns: "",
  sort: "featured",
  type: "",
} as const;

/**
 * @todo
 * Drop state logic, remove `filterAndSortListings` & simplify this
 * hook once `algoliaFiltering` feature flag is removed.
 */
export const useListingFilterAndSort = (
  listings: ShopListingModel[],
  treatmentType: ListingTypeOptions | null
) => {
  const stringifyArray = (strings: string[] | null) => {
    return strings?.join(",") ?? "";
  };

  const joinStrings = (strings: string | null) => {
    return strings === "" ? [] : strings?.split(",") ?? [];
  };

  const [searchParams, setSearchParams] = useSearchParams(
    DEFAULT_SEARCH_PARAMS
  );

  const [type, setType] = useState<ListingTypeOptions | null>(
    searchParams.get("type") == ""
      ? treatmentType
      : (searchParams.get("type") as ListingTypeOptions | null)
  );
  const [concerns, setConcerns] = useState<string[]>(
    joinStrings(searchParams.get("concerns"))
  );
  const [areas, setAreas] = useState<string[]>(
    joinStrings(searchParams.get("areas"))
  );
  const [sort, sortBy] = useState<ListingSortOptions>(
    searchParams.get("sort") as ListingSortOptions
  );
  const [checkpoints, setCheckpoints] = useState({
    type,
    concerns,
    areas,
    sort,
  });

  const typeCount = listings
    .flatMap((listing) => listing.type)
    .reduce((acc, concernId) => {
      return { ...acc, [concernId]: (acc[concernId] ?? 0) + 1 };
    }, {} as Record<string, number>);

  const filteredListings = filterAndSortListings(
    listings,
    type,
    concerns,
    areas,
    sort
  );

  const concernsCount = filteredListings
    .flatMap(({ concernIds }) => concernIds)
    .reduce((acc, concernId) => {
      return { ...acc, [concernId]: (acc[concernId] ?? 0) + 1 };
    }, {} as Record<string, number>);

  const areasCount = filteredListings
    .flatMap(({ areaIds }) => areaIds)
    .reduce((acc, areaId) => {
      return { ...acc, [areaId]: (acc[areaId] ?? 0) + 1 };
    }, {} as Record<string, number>);

  const hasListingsWithDiscounts = filteredListings.some(
    ({ discount }) => discount?.maxAmountOff || discount?.maxPercentageOff
  );
  let filteredSortOptions = sortOptions;
  if (!hasListingsWithDiscounts) {
    filteredSortOptions = new Map(
      Array.from(sortOptions).filter(([key]) => key !== "onsale")
    );
  }
  if (sort === "onsale" && !hasListingsWithDiscounts) {
    sortBy("featured");
  }

  return {
    filteredListings,
    type,
    setType,
    concerns,
    setConcerns: (concernId: string) => {
      let newConcerns;
      if (concerns.includes(concernId)) {
        newConcerns = concerns.filter((concern) => concern !== concernId);
      } else {
        newConcerns = [...concerns, concernId];
      }
      setConcerns(newConcerns);
    },
    areas,
    setAreas: (areaId: string) => {
      let newAreas;
      if (areas.includes(areaId)) {
        newAreas = areas.filter((area) => area !== areaId);
      } else {
        newAreas = [...areas, areaId];
      }
      setAreas(newAreas);
    },
    sort,
    sortBy,
    sortOptions: filteredSortOptions,
    setSearchParams: () =>
      setSearchParams({
        areas: stringifyArray(areas),
        concerns: stringifyArray(concerns),
        sort: sort,
        type: type ?? "",
      }),
    typeCount,
    concernsCount,
    areasCount,
    saveCheckpoints: () => setCheckpoints({ type, concerns, areas, sort }),
    restore: () => {
      setType(checkpoints.type);
      setConcerns(checkpoints.concerns);
      setAreas(checkpoints.areas);
      sortBy(checkpoints.sort);
    },
    clearConcerns: () => {
      setConcerns([]);
      setSearchParams({
        areas: searchParams.get("areas") ?? "",
        sort: searchParams.get("sort") ?? "",
        type: searchParams.get("type") ?? "",
      });
    },
    clearAreas: () => {
      setAreas([]);
      setSearchParams({
        concerns: searchParams.get("concerns") ?? "",
        sort: searchParams.get("sort") ?? "",
        type: searchParams.get("type") ?? "",
      });
    },
  };
};

export const filterAndSortListings = (
  listings: ShopListingModel[],
  type: ListingTypeOptions | null,
  concerns: string[],
  areas: string[],
  sort: ListingSortOptions
) => {
  return listings
    .filter((listing) => (type == null ? true : listing.type == type))
    .filter((listing) =>
      concerns.every((concern) => listing.concernIds.includes(concern))
    )
    .filter((listing) => areas.every((area) => listing.areaIds.includes(area)))
    .sort((a, b) => {
      switch (sort) {
        case "featured": {
          return 0;
        }
        case "priceLowToHigh": {
          return a.baseCost - b.baseCost;
        }
        case "priceHighToLow": {
          return b.baseCost - a.baseCost;
        }
        case "recentlyAdded": {
          return new Date(b.created).valueOf() - new Date(a.created).valueOf();
        }
        case "onsale": {
          /**
          Sort by on sale items in the following order:
          1. Items with the highest percentage-off discount at the top.
          2. Next, items with the highest dollar-off discount.
          3. Finally, display services as configured by the admin settings (order).
          */
          const maxPercentageOffA = a.discount?.maxPercentageOff
            ? a.discount.maxPercentageOff * 100
            : null;
          const maxPercentageOffB = b.discount?.maxPercentageOff
            ? b.discount.maxPercentageOff * 100
            : null;
          if (maxPercentageOffA && maxPercentageOffB) {
            return maxPercentageOffB - maxPercentageOffA;
          }

          if (maxPercentageOffA || maxPercentageOffB) {
            return maxPercentageOffA != null ? -1 : 1;
          }

          if (a.discount?.maxAmountOff && b.discount?.maxAmountOff) {
            return b.discount.maxAmountOff - a.discount.maxAmountOff;
          }

          if (a.discount?.maxAmountOff || b.discount?.maxAmountOff) {
            return a.discount?.maxAmountOff != null ? -1 : 1;
          }

          return 0;
        }
        case "bestsellers":
        default: {
          return b.sellVolume - a.sellVolume;
        }
      }
    });
};
